import React, { useState, useEffect } from 'react';
import { readingPlansApi, readerApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PlanCard from '../components/reading-plans/PlanCard';
import PlanProgress from '../components/reading-plans/PlanProgress';
import StartPlanModal from '../components/reading-plans/StartPlanModal';
import './ReadingPlansPage.css';

const ReadingPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [readers, setReaders] = useState([]);
  const [readerProgress, setReaderProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedReader, setSelectedReader] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load plans and readers in parallel
      const [plansResponse, readersResponse] = await Promise.all([
        readingPlansApi.getPlans(),
        readerApi.getReaders()
      ]);

      setPlans(plansResponse.data.plans || []);
      setReaders(readersResponse.data.readers || []);

      // Load progress for each reader
      const progressPromises = readersResponse.data.readers.map(async (reader) => {
        try {
          const progressResponse = await readingPlansApi.getReaderProgress(reader.id);
          return { readerId: reader.id, progress: progressResponse.data.progress || [] };
        } catch (error) {
          console.warn(`Failed to load progress for reader ${reader.id}:`, error);
          return { readerId: reader.id, progress: [] };
        }
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(({ readerId, progress }) => {
        progressMap[readerId] = progress;
      });

      setReaderProgress(progressMap);
    } catch (err) {
      console.error('Error loading reading plans data:', err);
      setError('Failed to load plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPlan = (plan, reader) => {
    setSelectedPlan(plan);
    setSelectedReader(reader);
    setShowStartModal(true);
  };

  const handlePlanStarted = async (planId, readerId, options) => {
    try {
      await readingPlansApi.startPlan(readerId, planId, options);
      
      // Reload progress for this reader
      const progressResponse = await readingPlansApi.getReaderProgress(readerId);
      setReaderProgress(prev => ({
        ...prev,
        [readerId]: progressResponse.data.progress || []
      }));

      setShowStartModal(false);
      setSelectedPlan(null);
      setSelectedReader(null);
    } catch (err) {
      console.error('Error starting plan:', err);
      throw err;
    }
  };

  const getPlanProgress = (readerId, planId) => {
    const readerPlans = readerProgress[readerId] || [];
    return readerPlans.find(progress => progress.planId === planId);
  };

  if (loading) {
    return (
      <div className="reading-plans-page">
        <div className="loading-container">
          <LoadingSpinner size="large" message="Loading plans..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reading-plans-page">
        <div className="error-container">
          <h2>Unable to load plans</h2>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reading-plans-page">
      <div className="reading-plans-header">
        <h1>Plans</h1>
        <p>Choose a plan to help guide your Bible reading journey</p>
      </div>

      {readers.length === 0 ? (
        <div className="no-readers-message">
          <h3>No reader yet</h3>
          <p>Add a reader under Family → Readers before you can start a plan.</p>
          <a href="/family#readers" className="create-reader-button">
            Go to Family → Readers
          </a>
        </div>
      ) : (
        <div className="reading-plans-content">
          <div className="plans-section">
            <h2>All plans</h2>
            <div className="plans-grid">
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  readers={readers}
                  onStartPlan={handleStartPlan}
                  getPlanProgress={getPlanProgress}
                />
              ))}
            </div>
          </div>

          <div className="progress-section">
            <h2>Active plans</h2>
            {readers.map(reader => {
              const readerPlans = readerProgress[reader.id] || [];
              if (readerPlans.length === 0) {
                return null;
              }

              return (
                <div key={reader.id} className="reader-progress">
                  <h3>{reader.name}</h3>
                  <div className="progress-cards">
                    {readerPlans.map(progress => {
                      const plan = plans.find(p => p.id === progress.planId);
                      if (!plan) return null;

                      return (
                        <PlanProgress
                          key={progress.id}
                          plan={plan}
                          progress={progress}
                          reader={reader}
                          onRefresh={loadData}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showStartModal && selectedPlan && selectedReader && (
        <StartPlanModal
          plan={selectedPlan}
          reader={selectedReader}
          onStart={handlePlanStarted}
          onClose={() => {
            setShowStartModal(false);
            setSelectedPlan(null);
            setSelectedReader(null);
          }}
        />
      )}
    </div>
  );
};

export default ReadingPlansPage;
